import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { complianceApi, completenessRulesApi, type ComplianceScope } from '../api/completeness';
import { ApiError } from '../api/client';
import type {
  CompletenessEntityType,
  CompletenessRule,
  CompletenessRuleCreateInput,
  CompletenessRuleUpdateInput,
  ExpiringReport,
  FieldMissingReport,
  IncompleteReport,
  Page,
  UUID,
} from '../api/types';

const cKeys = {
  rules: ['completeness-rules'] as const,
  rulesByType: (et?: CompletenessEntityType) =>
    ['completeness-rules', 'by-type', et] as const,
  incomplete: (et?: CompletenessEntityType, includeInactive: boolean = false,
    scope: ComplianceScope = 'all') =>
    ['compliance', 'incomplete', et, includeInactive, scope] as const,
  fieldsMissing: (et: CompletenessEntityType, id: UUID) =>
    ['compliance', 'incomplete-fields', et, id] as const,
};

export function useCompletenessRules(entity_type?: CompletenessEntityType) {
  return useQuery<Page<CompletenessRule>, ApiError>({
    queryKey: cKeys.rulesByType(entity_type),
    queryFn: () => completenessRulesApi.list(entity_type),
  });
}

export function useCreateCompletenessRule() {
  const qc = useQueryClient();
  return useMutation<CompletenessRule, ApiError, CompletenessRuleCreateInput>({
    mutationFn: completenessRulesApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cKeys.rules });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}

export function useUpdateCompletenessRule(id: UUID) {
  const qc = useQueryClient();
  return useMutation<CompletenessRule, ApiError, CompletenessRuleUpdateInput>({
    mutationFn: (input) => completenessRulesApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cKeys.rules });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}

export function useDeleteCompletenessRule() {
  const qc = useQueryClient();
  return useMutation<CompletenessRule, ApiError, UUID>({
    mutationFn: completenessRulesApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: cKeys.rules });
      qc.invalidateQueries({ queryKey: ['compliance'] });
    },
  });
}

export function useIncompleteRecords(
  entity_type?: CompletenessEntityType,
  include_inactive: boolean = false,
  scope: ComplianceScope = 'all',
  enabled: boolean = true,
) {
  return useQuery<IncompleteReport, ApiError>({
    queryKey: cKeys.incomplete(entity_type, include_inactive, scope),
    queryFn: () => complianceApi.incomplete(entity_type, include_inactive, scope),
    enabled,
  });
}

export function useFieldsMissing(
  entity_type: CompletenessEntityType,
  id: UUID | undefined,
) {
  return useQuery<FieldMissingReport, ApiError>({
    queryKey: cKeys.fieldsMissing(entity_type, id ?? '' as UUID),
    queryFn: () => complianceApi.fieldsMissing(entity_type, id!),
    enabled: !!id,
  });
}

export function useExpiring(
  within_months: number, include_inactive: boolean = false,
  scope: ComplianceScope = 'all', enabled: boolean = true,
) {
  return useQuery<ExpiringReport, ApiError>({
    queryKey: ['compliance', 'expiring', within_months, include_inactive, scope],
    queryFn: () => complianceApi.expiring(within_months, include_inactive, scope),
    enabled,
  });
}
